import { z } from "zod";
import type { FieldType } from "@/lib/dynamicForms/interfaces/dynamicForm.interface";

export enum LayoutTypes {
	VerticalLayout = "VerticalLayout",
	HorizontalLayout = "HorizontalLayout",
	Group = "Group",
	Field = "Field",
}

/**
 * JSON Forms–inspired layout node: vertical stack of child elements.
 */
export interface VerticalLayoutElement {
	type: LayoutTypes.VerticalLayout;
	elements: LayoutElement[];
}

/**
 * JSON Forms–inspired layout node: horizontal row of child elements.
 */
export interface HorizontalLayoutElement {
	type: LayoutTypes.HorizontalLayout;
	elements: LayoutElement[];
}

/**
 * Group with optional heading. When `enabled` is true, runtime renders a collapsible
 * accordion (`LayoutGroupAccordion`); `defaultExpanded` sets the initial open state.
 * When `enabled` is false, runtime renders a static titled section (always expanded).
 * `maxItems` is accepted for saved layouts but ignored at render time (no cap).
 */
export interface GroupLayoutElement {
	type: LayoutTypes.Group;
	/** Stable id for builder round-trip (section node id). */
	id?: string;
	label?: string;
	/** When true, section is collapsible (form-builder "Collapsible"). */
	enabled: boolean;
	/** Initial open state when `enabled` is true (form-builder "Expanded"). */
	defaultExpanded: boolean;
	maxItems?: number;
	elements: LayoutElement[];
}

export interface LayoutInfo {
	fieldType: FieldType;
	disabled?: boolean;
	hidden?: boolean;
	required?: boolean;
	metadata?: Record<string, unknown>;
	/** Optional embedded field payload in layout JSON; parse does not narrow shape. */
	field?: unknown;
}

/**
 * Leaf node that binds a layout slot to a field via `fieldDefinitionId` (definition `id` or `name`).
 */
export interface FieldLayoutElement {
	type: LayoutTypes.Field;
	/** Bind by OpenAPI `FieldDefinition.id` (UUID) or by `FieldDefinition.name`. */
	fieldDefinitionId: string;
	layoutInfo?: LayoutInfo;
}

/**
 * Discriminated union of all layout nodes accepted by {@link parseFormLayoutJson}.
 */
export type LayoutElement =
	| VerticalLayoutElement
	| HorizontalLayoutElement
	| GroupLayoutElement
	| FieldLayoutElement;

/** Matches {@link FieldType} for {@link LayoutInfo.fieldType} in serialized layout JSON. */
const layoutFieldTypeSchema = z.enum([
	"text",
	"textarea",
	"number",
	"phone",
	"url",
	"checkbox",
	"radio",
	"select",
	"slider",
	"switch",
	"toggleButton",
	"autocomplete",
	"group",
	"fieldArray",
	"date",
	"time",
	"dateTime",
	"file",
	"multiSelect",
	"users",
	"signature",
	"address",
]);

const layoutInfoSchema = z
	.object({
		fieldType: layoutFieldTypeSchema,
		disabled: z.boolean().optional(),
		hidden: z.boolean().optional(),
		required: z.boolean().optional(),
		metadata: z.record(z.string(), z.unknown()).optional(),
		field: z.unknown().optional(),
	})
	.strict();

const layoutElementSchema: z.ZodType<LayoutElement> = z.lazy(() =>
	z.discriminatedUnion("type", [
		z.object({
			type: z.literal(LayoutTypes.VerticalLayout),
			elements: z.array(layoutElementSchema),
		}),
		z.object({
			type: z.literal(LayoutTypes.HorizontalLayout),
			elements: z.array(layoutElementSchema),
		}),
		z.object({
			type: z.literal(LayoutTypes.Group),
			id: z.string().optional(),
			label: z.string().optional(),
			enabled: z.boolean(),
			defaultExpanded: z.boolean(),
			maxItems: z.number().optional(),
			elements: z.array(layoutElementSchema),
		}),
		z.object({
			type: z.literal(LayoutTypes.Field),
			fieldDefinitionId: z.string(),
			layoutInfo: layoutInfoSchema.optional(),
		}),
	]),
);

export { layoutElementSchema };

/**
 * Parses and validates a layout JSON string (e.g. from `FormDefinition.layoutJson`).
 *
 * @param json - Raw JSON string.
 * @returns Parsed layout or an error.
 */
export function parseFormLayoutJson(
	json: string,
): { ok: true; layout: LayoutElement } | { ok: false; error: Error } {
	try {
		const raw: unknown = JSON.parse(json);
		return parseFormLayoutUnknown(raw);
	} catch (e) {
		const err = e instanceof Error ? e : new Error(String(e));
		return { ok: false, error: err };
	}
}

/**
 * Validates an already-parsed JSON value as a layout tree.
 *
 * @param data - Unknown value (typically from `JSON.parse`).
 * @returns Parsed layout or an error.
 */
export function parseFormLayoutUnknown(
	data: unknown,
): { ok: true; layout: LayoutElement } | { ok: false; error: Error } {
	const result = layoutElementSchema.safeParse(data);
	if (!result.success) {
		return {
			ok: false,
			error: new Error(z.prettifyError(result.error)),
		};
	}
	if (result.data == null) {
		return {
			ok: false,
			error: new Error("Layout validation produced no data"),
		};
	}
	return { ok: true, layout: result.data };
}
